import { useNavigate } from '@tanstack/react-router';
import { composeNavigation, groupNavigation, MODULE_NAVIGATION } from '@/app/config/navigation';
import { getWorkspaceResolution } from '@/app/workspace/store';
import type { WorkspaceScope } from '@/app/workspace/types';
import { useSession } from '@/app/session';
import { performSignOut } from '@/app/session';
import { authApi } from '@/modules/auth/services/api';
import { checkPermissions } from '@/shared/permissions';
import type { PermissionSpec } from '@/shared/permissions';
import type { SidebarNavItem } from '@/shared/layout/sidebar/types';
import type { AccountPanelGroup, ProfileIdentity } from '@/shared/layout/profile-panel';
import {
  accountPanelMenuItems,
  ACCOUNT_PANEL_SETTINGS,
  WORKSPACE_SETTINGS_NAV_ID,
} from '@/layouts/app-shell/components/ui-store/menu-source';
import { useSidebarCollapsed, useToggleSidebar, useMobileNavOpen, useSetMobileNavOpen } from '@/layouts/app-shell/components/ui-store';

/** Icon names for the menu, resolved through the registry rather than imported. */
const SHORTCUT_ICONS: Record<string, string> = {
  'platform.tenants': 'users',
  'catalog.products': 'package',
  'bookings.appointments': 'calendar',
};

export function useAppShellProps() {
  const { user } = useSession();
  const navigate = useNavigate();
  const scope = resolveScope();

  const granted = user?.permissions ?? [];
  const platformGranted = user?.platformPermissions ?? [];
  const capabilities = capabilitiesFor(user, scope);

  // Compose navigation from the registry, filtered by capabilities and permissions.
  // The TWO permission axes are passed separately: a tenant permission and a
  // platform grant are different powers, and the registry says which one each
  // destination needs.
  const items = sidebarItemsFor(granted, platformGranted, capabilities, scope);

  const collapsed = useSidebarCollapsed();
  const toggleSidebar = useToggleSidebar();
  const mobileNavOpen = useMobileNavOpen();
  const setMobileNavOpen = useSetMobileNavOpen();

  // One visibility predicate for both panel bands, so a destination cannot be
  // offered by the grid and withheld by the rows.
  const mayReach = reachability(granted, platformGranted, capabilities);

  return {
    sidebarProps: {
      items,
      collapsed,
      open: mobileNavOpen,
      onClose: () => setMobileNavOpen(false),
    },
    bottomNavProps: {
      items: items.filter((item) => item.mobile),
    },
    sidebarToggleProps: togglePropsFor(collapsed, toggleSidebar, setMobileNavOpen),
    identity: identityFor(user?.displayName, scope, user?.isPlatformOperator),
    accountPanelJumpTo: accountPanelJumpToFor(mayReach),
    accountPanelGroups: accountPanelGroupsFor(granted, platformGranted, capabilities),
    onSignOut: () => signOut(navigate),
  };
}

/** The resolved workspace scope, or null when the workspace is not resolved. */
function resolveScope(): WorkspaceScope | null {
  const resolution = getWorkspaceResolution();
  return resolution.status === 'resolved' ? resolution.scope : null;
}

/**
 * The capabilities the sidebar filters on.
 *
 * The WORKSPACE's entitlements win over the session's own list: the workspace is
 * the tenant whose features are being offered, and the session list is the same
 * information carried a second way. Both come from the server.
 */
function capabilitiesFor(
  user: { capabilities?: ReadonlyArray<string> } | null | undefined,
  scope: WorkspaceScope | null,
): ReadonlyArray<string> {
  return scope?.capabilities ?? user?.capabilities ?? [];
}

/**
 * The sidebar toggle's behaviour.
 *
 * On a narrow viewport the toggle OPENS the mobile drawer; on a wide one it
 * collapses the rail. The two are different actions, and conflating them is
 * how a phone-sized drawer ends up toggling a hidden rail instead.
 */
function togglePropsFor(
  collapsed: boolean,
  toggleSidebar: () => void,
  setMobileNavOpen: (open: boolean) => void,
) {
  return {
    onClick: () => {
      if (window.innerWidth < 1024) {
        setMobileNavOpen(true);
      } else {
        toggleSidebar();
      }
    },
    'aria-label': collapsed ? 'Show sidebar' : 'Hide sidebar',
    'aria-expanded': !collapsed,
  };
}

/** The panel's jump-to rows — the destinations offered as shortcuts. */
function accountPanelJumpToFor(mayReach: (item: PanelDestination) => boolean) {
  return accountPanelMenuItems
    .filter(mayReach)
    .map((item) => ({
      to: item.to,
      label: item.label,
      description: item.description,
      icon: SHORTCUT_ICONS[item.id],
    }));
}

/**
 * The sidebar's flat destination list.
 *
 * The registry is composed into GROUPS and then flattened back out with the
 * group name attached, because the sidebar renders a labelled section per
 * group. The composition and the flattening live together so the two can never
 * disagree about what is offered.
 */
function sidebarItemsFor(
  permissions: ReadonlyArray<string>,
  platformPermissions: ReadonlyArray<string>,
  capabilities: ReadonlyArray<string>,
  scope: WorkspaceScope | null,
): SidebarNavItem[] {
  const grouped = groupNavigation(
    composeNavigation({ permissions, platformPermissions, capabilities }),
  );

  return grouped.flatMap((group) =>
    group.items.flatMap((item) => {
      const to = resolveDestination(item.to, scope);
      // A destination the shell cannot address is not offered at all. A link that
      // goes nowhere is worse than no link: it looks like a working destination.
      if (to === null) return [];
      return [{ ...item, to, section: group.group }];
    }),
  );
}

/**
 * Resolve a registry destination against the resolved workspace.
 *
 * The registry stores a TEMPLATE for a tenant-scoped destination — the same route
 * for every tenant — and the shell fills the parameter in from the scope it has
 * already resolved. An unresolved template handed to the router renders as
 * `/tenant/undefined/workspace/ai`: a link that LOOKS like a link and goes
 * nowhere, and a row that is never highlighted, because the rendered path never
 * matches the route.
 *
 * The API resolves the tenant from the caller's session, so the segment NAMES
 * the tenant rather than selecting it — but a name still has to be a real one.
 */
function resolveDestination(to: string, scope: WorkspaceScope | null): string | null {
  if (!to.includes('$tenantId')) return to;
  const tenantId = scope?.tenant.id;
  if (tenantId === undefined || tenantId === '') return null;
  return to.replace('$tenantId', tenantId);
}

/**
 * A destination's gates, as the shell checks them.
 *
 * `permissionScope` says which authority axis carries `permission`. It is part
 * of the shape rather than a detail of the check because a destination checked
 * on the WRONG axis is a destination offered to someone who cannot reach it.
 */
type PanelDestination = {
  permission?: PermissionSpec;
  capability?: string;
  permissionScope?: 'tenant' | 'platform';
};

/**
 * The visibility predicate for a panel destination.
 *
 * The SAME permission and capability check the sidebar uses, so the panel can
 * never offer a destination the user would be bounced out of. That check is the
 * reason the panel's contents are derived rather than hard-coded: a literal
 * list keeps offering "Tenants" to someone who lost tenancy access.
 */
function reachability(
  permissions: ReadonlyArray<string>,
  platformPermissions: ReadonlyArray<string>,
  capabilities: ReadonlyArray<string>,
) {
  return (item: PanelDestination): boolean => {
    if (item.capability && !capabilities.includes(item.capability)) return false;
    if (item.permission) {
      const axis = item.permissionScope === 'platform' ? platformPermissions : permissions;
      if (!checkPermissions(axis, item.permission)) return false;
    }
    return true;
  };
}

/**
 * The panel's described rows — destinations whose label alone does not say
 * enough to choose between them.
 *
 * Banded separately from the grid because the two are different reading modes,
 * not two lists. The grid is scanned by recognition; these are read.
 */
function accountPanelGroupsFor(
  permissions: ReadonlyArray<string>,
  platformPermissions: ReadonlyArray<string>,
  capabilities: ReadonlyArray<string>,
): ReadonlyArray<AccountPanelGroup> {
  const mayReach = reachability(permissions, platformPermissions, capabilities);

  const settingsNav = MODULE_NAVIGATION.find((item) => item.id === WORKSPACE_SETTINGS_NAV_ID);
  if (!settingsNav || !mayReach(settingsNav)) return [];

  return [
    {
      label: 'This workspace',
      items: [
        {
          // Route and label both come from the registry. This is the one row the
          // panel and the sidebar must agree about word-for-word, and two typed
          // copies of a label is exactly how they stop agreeing.
          to: settingsNav.to,
          label: settingsNav.label,
          description: ACCOUNT_PANEL_SETTINGS.description,
          icon: ACCOUNT_PANEL_SETTINGS.icon,
        },
      ],
    },
  ];
}

/**
 * The identity the shell renders. Both inputs are server-resolved; nothing is
 * derived from a claim the client made itself.
 *
 * The role is a plain string rather than a role enum, because "platform
 * operator" is a UX distinction — it decides that a badge reads "Platform
 * administrator" — and never an authorization decision. The server authorizes
 * every request regardless of what this says.
 *
 * The workspace name falls back to the TENANT slug. The session projection
 * resolves the tenant and the actor's authority in it; it does not report an
 * organisation or a workspace, and inventing a name there would put a word in
 * the shell that the server never said.
 */
function identityFor(
  displayName: string | undefined,
  scope: WorkspaceScope | null,
  isPlatformOperator: boolean | undefined,
): ProfileIdentity {
  return {
    name: displayName ?? 'Signed out',
    role: isPlatformOperator ? 'Platform administrator' : 'Workspace member',
    workspaceName: scope?.workspace?.name ?? scope?.tenant.slug ?? 'No workspace',
  };
}

/**
 * Signs out through the canonical path.
 *
 * Not `resetSession()` directly: sign-out also tells other tabs and the server,
 * and the ordering that makes it safe — clear locally first, report honestly
 * whether the server agreed — lives in `performSignOut`. Re-implementing two of
 * its four steps here is how a stale token survives in a second tab.
 *
 * The provider's sign-out is the whole server-side step. It deletes the provider's
 * session, and the API's attestation reads that session, so the session is dead
 * from the platform's point of view too.
 *
 * The platform's own revocation route (`POST /api/v1/identity/sessions/revoke`) is
 * deliberately NOT called here. Its reason vocabulary is `compromise` and
 * `offboarding` — administrative events — and writing `offboarding` because a user
 * clicked "sign out" would put a false statement in the audit log.
 *
 * AND THEN THE CONSOLE LEAVES
 * ---------------------------
 * The local session is cleared unconditionally, so the user is no longer signed
 * in — and a console that stays on the page they were reading is a console that
 * still looks signed in. The navigation to the sign-in screen happens whatever
 * the server said: the local state is already gone, and the screen must say so.
 */
function signOut(navigate: (options: { to: '/login' }) => void): void {
  void performSignOut(async () => {
    await authApi.signOut();
  }).then(() => {
    navigate({ to: '/login' });
  });
}